import { useCallback, useEffect, useMemo, useState } from 'react';
import { PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import Typography from '@material-ui/core/Typography';
import BusinessLocationIcon from 'assets/images/ion_business-location.svg';

function getColorKey(color) {
  return color || undefined;
}


export const useDndEquipmentOrderItems = ({
  colorGroups,
  equipmentOrderInColorGroups,
  equipment,
  locations,
  onMoveEquipment,
  onMoveColorGroup,
  openConfirmDialog,
}) => {
  const [dropIndicator, setDropIndicator] = useState(); // { itemId, position }

  const [groups, setGroups] = useState(() => {
    const sortedEquipmentOrderInColorGroup = structuredClone(equipmentOrderInColorGroups)
      .sort((a, b) => (a.orderInColorGroup - b.orderInColorGroup));

    const groups = {};
    sortedEquipmentOrderInColorGroup.forEach((equipmentOrder) => {
      const key = getColorKey(equipmentOrder.color);
      if (!key) return;
      const equip = equipment.find(equip => equip._id === equipmentOrder.equipmentId);
      if (!equip) return;

      (groups[key] = groups[key] || []).push({ ...equipmentOrder, equipment: equip });
    });
    return groups;
  });

  const sortedGroups = useMemo(
    () => structuredClone(colorGroups).sort((a, b) => a.orderNumber - b.orderNumber),
    [colorGroups]
  )

  useEffect(() => {
    const sortedEquipmentOrderInColorGroup = structuredClone(equipmentOrderInColorGroups)
      .sort((a, b) => (a.orderInColorGroup - b.orderInColorGroup));

    const groups = {};
    sortedEquipmentOrderInColorGroup.forEach((equipmentOrder) => {
      const key = getColorKey(equipmentOrder.color);
      if (!key) return;
      const equip = equipment.find(equip => equip._id === equipmentOrder.equipmentId);
      if (!equip) return;
      (groups[key] = groups[key] || []).push({ ...equipmentOrder, equipment: equip });
    });

    setGroups(groups)
  }, [colorGroups, equipmentOrderInColorGroups, equipment])

  // For DnD
  const [activeGroup, setActiveGroup] = useState(null); // group color
  const [activeEquipment, setActiveEquipment] = useState(null); // equipment id

  // Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor),
  );


  function handleDragStart(event) {
    const { active } = event;
    if (active.data.current?.type === 'group') {
      setActiveGroup(sortedGroups.find(group => group._id === active.id));
    } else if (active.data.current?.type === 'equipment') {
      setActiveEquipment(active.id);
    }
  }

  function handleDragEnd(event) {
    setDropIndicator(null);
    const { active, over, collisions } = event;
    setActiveGroup(null);
    setActiveEquipment(null);

    if (!over) return;
    const overCollision = collisions.find(c => c.id === over.id);

    // Drag group reorder
    if (active.data.current?.type === 'group' && over.data.current?.type === 'group') {
      if (active.id !== over.id) {
        const oldIndex = sortedGroups.findIndex(group => group._id === active.id);
        let newIndex = sortedGroups.findIndex(group => group._id === over.id);
        const newGroupOrder = arrayMove(sortedGroups, oldIndex, newIndex);
        // Re-build groups by new order
        const newGroups = {};
        newGroupOrder.forEach((group) => {
          newGroups[group.color] = groups[group.color];
        });
        setGroups(newGroups);

        if (oldIndex < newIndex) newIndex -= 1;
        if (oldIndex !== newIndex) {
          onMoveColorGroup && onMoveColorGroup(active.id, newIndex);
        }
      }
    }

    // Drag equipment (item) reorder or move
    if (active.data.current?.type === 'equipment') {
      let sourceColor, targetColor, sourceIdx, targetIdx;
      for (const color in groups) {
        const idx = groups[color].findIndex((eq) => eq.equipmentId === active.id);
        if (idx !== -1) {
          sourceColor = color;
          sourceIdx = idx;
          break;
        }
      }
      // Drop target is a item
      if (over.data.current?.type === 'equipment') {
        const currentEquipment = equipment.find((equip) => equip._id === active.id)

        if (!currentEquipment) return;
        for (const color in groups) {
          const idx = groups[color].findIndex((eq) => eq.equipmentId === over.id);
          if (idx !== -1) {
            targetColor = color;
            targetIdx = idx;
            break;
          }
        }
        if (sourceColor && targetColor) {
          let onMove = () => {};
          if (sourceColor === targetColor) {
            // reorder within
            if (sourceIdx !== targetIdx) {
              if (sourceIdx > targetIdx && overCollision?.data?.position === 'after') {
                targetIdx += 1;
              }
              if (sourceIdx < targetIdx && overCollision?.data?.position === 'before') {
                targetIdx -= 1;
              }

              onMove = () => {
                const newItems = arrayMove(groups[sourceColor], sourceIdx, targetIdx);
                setGroups({ ...groups, [sourceColor]: newItems });
              }
            }
          } else {
            if (overCollision?.data?.position === 'after') {
              targetIdx += 1;
            }
            // move to another group/insert at targetIdx
            onMove = () => {
              const movingItem = groups[sourceColor][sourceIdx];
              const newSource = [...groups[sourceColor]];
              newSource.splice(sourceIdx, 1);
              const newTarget = [...groups[targetColor]];
              newTarget.splice(targetIdx, 0, movingItem);
              setGroups({ ...groups, [sourceColor]: newSource, [targetColor]: newTarget });
            }
          }

          if (sourceColor !== targetColor && onMoveEquipment && openConfirmDialog) {
            let title = '', text = '';
            let renderContent = null;

            if (currentEquipment.assignedToLocations?.length > 1 ) {
              const assignedLocationNames = currentEquipment.assignedToLocations
                .map((locationId) => locations.find(({ _id }) => _id === locationId))
                .filter(location => !!location?.name)
                .map(({ name }) => name)
              title = `This equipment is assigned to multiple locations:`;
              renderContent = () => (
                <>
                  {assignedLocationNames.map((name) => (
                    <Typography variant="h6" style={{ color: '#3e82ff' }}>
                      <img src={BusinessLocationIcon} alt="location" style={{ marginRight: '6px' }} />
                      {name}
                    </Typography>
                  ))}
                  <Typography variant="h6" style={{ marginTop: '6px' }}>Changing this equipment’s color will apply to all the listed locations.</Typography>
                  <Typography variant="h6">Do you still want to continue?</Typography>
                </>
              )
            } else {
              title = 'Moving this card will change its color';
              text = 'Do you want to continue?'
            }

            openConfirmDialog({
              title,
              text,
              renderContent,
              onSubmit: async () => {
                onMove();
                await onMoveEquipment(active.id, targetColor, targetIdx)
              }
            })
          } else if (
            sourceColor !== targetColor ||
            (sourceColor === targetColor && sourceIdx !== targetIdx)
          ) {
            onMove();
            onMoveEquipment && onMoveEquipment(active.id, targetColor, targetIdx);
          }
        }
      }
    }
  }

  const handleDragMove = useCallback(
    (event) => {
      const { active, over, collisions } = event;
      
      if (!over || active.data.current?.type !== 'equipment') {
        setDropIndicator(null);
        return;
      }

      const overCollision = collisions.find(c => c.id === over.id);

      if (!overCollision) {
        setDropIndicator(null);
        return;
      }

      if (dropIndicator?.itemId !== over.id || dropIndicator?.position !== overCollision.data?.position) {
        setDropIndicator({ itemId: over.id, position: overCollision.data?.position });
      }
    }, [dropIndicator]
  )
  

  const handleDragCancel = useCallback(() => {
    setDropIndicator(null);
    setActiveGroup(null);
    setActiveEquipment(null);
  }, []);

  return { groups, sortedGroups, activeGroup, activeEquipment, sensors, dropIndicator, handleDragStart, handleDragEnd, handleDragCancel, handleDragMove };
};
